<template>
  <div class="admin-auth-root min-vh-100 d-flex flex-column justify-content-between p-3 p-md-4">
    <!-- Top Navigation Bar -->
    <div class="d-flex justify-content-between align-items-center mb-3 px-1">
      <div class="d-flex align-items-center gap-2">
        <span class="system-status-pill">
          <span class="status-dot"></span>
          <span>{{ $currentLocale === 'en' ? 'Enterprise Workspace Onboarding' : 'Registrasi Ruang Kerja Perusahaan' }}</span>
        </span>
      </div>
      <div class="d-flex align-items-center gap-2">
        <!-- Language Switcher -->
        <div class="lang-switch-group">
          <button
            type="button"
            class="lang-btn"
            :class="{ active: $currentLocale === 'en' }"
            @click="$setLocale('en')"
          >
            EN
          </button>
          <span class="lang-divider">/</span>
          <button
            type="button"
            class="lang-btn"
            :class="{ active: $currentLocale === 'id' }"
            @click="$setLocale('id')"
          >
            ID
          </button>
        </div>

        <router-link :to="{ name: 'Login' }" class="player-link">
          {{ $t('auth.signInBtn') }} <span class="arrow">→</span>
        </router-link>
      </div>
    </div>

    <!-- Centered Registration Card -->
    <div class="d-flex align-items-center justify-content-center flex-grow-1 py-3">
      <div class="register-container w-100" style="max-width: 960px;">
        <div class="card border-0 shadow-lg" style="background: #191c24; border: 1px solid #2c2e33; border-radius: 14px;">
          <!-- Card Header -->
          <div class="card-header text-center pt-4 pb-3 border-bottom" style="border-color: #2c2e33; background: transparent;">
            <router-link :to="{ name: 'Login' }">
              <img
                width="140"
                class="mx-auto mb-2"
                src="../assets/images/logo-big.svg"
                alt="Gamiteam"
              />
            </router-link>
            <h2 class="text-white font-weight-bold mb-1 h4">
              {{ $t('register.title') }}
            </h2>
            <p class="text-muted small mb-0 px-3" style="max-width: 650px; margin: 0 auto;">
              {{ $t('register.subtitle') }}
            </p>
          </div>

          <!-- Card Body -->
          <div class="card-body p-4 p-md-5">
            <form @submit.prevent="submitForm" enctype="multipart/form-data">

              <!-- SECTION 1: ONBOARDING PLAN SELECTION -->
              <div class="mb-4">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h6 class="text-warning text-uppercase font-weight-bold mb-0 d-flex align-items-center" style="letter-spacing: 0.8px; font-size: 12px;">
                    <i class="mdi mdi-shield-crown-outline mr-2" style="font-size: 16px;"></i>
                    1. {{ $currentLocale === 'en' ? 'Select Onboarding Plan' : 'Pilih Skema Pendaftaran' }}
                  </h6>
                  <span class="badge badge-outline-warning small">{{ $currentLocale === 'en' ? 'Self-Service Setup' : 'Aktivasi Mandiri' }}</span>
                </div>

                <div class="row g-3">
                  <!-- Option 1: 14-Day Free Trial -->
                  <div class="col-md-4">
                    <div
                      class="plan-card p-3 rounded h-100 cursor-pointer text-center"
                      :class="{ 'plan-selected': selectedPlan === 'trial' }"
                      @click="setPlan('trial')"
                    >
                      <span class="badge badge-success mb-2 px-2 py-1 font-weight-bold">
                        {{ $currentLocale === 'en' ? 'Instant Access' : 'Coba Gratis Instan' }}
                      </span>
                      <h5 class="font-weight-bold text-white mb-1">
                        {{ $currentLocale === 'en' ? '14-Day Free Trial' : 'Coba Gratis 14 Hari' }}
                      </h5>
                      <div class="h3 font-weight-bold text-warning mb-1">Rp 0</div>
                      <div class="text-muted small mb-2">15 {{ $t('quota.seats') }} • 14 {{ $currentLocale === 'en' ? 'Days' : 'Hari' }}</div>
                      <p class="text-light small mb-0" style="font-size: 11px;">
                        {{ $currentLocale === 'en' ? 'Instant workspace access. Full features without upfront payment.' : 'Akses instan tanpa bayar di awal. Eksplorasi misi, capaian beruntun, dan analitik.' }}
                      </p>
                    </div>
                  </div>

                  <!-- Option 2: Paketan Tier -->
                  <div class="col-md-4">
                    <div
                      class="plan-card p-3 rounded h-100 cursor-pointer text-center"
                      :class="{ 'plan-selected': selectedPlan === 'package' }"
                      @click="setPlan('package')"
                    >
                      <span class="badge badge-primary mb-2 px-2 py-1 font-weight-bold">
                        {{ $currentLocale === 'en' ? 'Bundled Tier' : 'Paket Berlangganan' }}
                      </span>
                      <h5 class="font-weight-bold text-white mb-1">
                        {{ selectedPackageObj?.package_name || 'Business Pro' }}
                      </h5>
                      <div class="h3 font-weight-bold text-info mb-1">
                        Rp {{ formatPrice(selectedPackageObj?.price_per_person_per_month || 49000) }}
                        <span class="text-muted small" style="font-size: 10px;">/{{ $t('quota.seats') }}</span>
                      </div>
                      <div class="text-muted small mb-2">{{ selectedPackageObj?.limit_user || 75 }} {{ $t('quota.seats') }}</div>
                      <p class="text-light small mb-0" style="font-size: 11px;">
                        {{ $currentLocale === 'en' ? 'Pre-bundled corporate tier with included duration discounts.' : 'Paket hemat berkapasitas siap pakai dengan diskon tahunan.' }}
                      </p>
                    </div>
                  </div>

                  <!-- Option 3: Custom Seats (Eceran) -->
                  <div class="col-md-4">
                    <div
                      class="plan-card p-3 rounded h-100 cursor-pointer text-center"
                      :class="{ 'plan-selected': selectedPlan === 'custom' }"
                      @click="setPlan('custom')"
                    >
                      <span class="badge badge-warning text-dark mb-2 px-2 py-1 font-weight-bold">
                        {{ $currentLocale === 'en' ? 'Custom Seats' : 'Fleksibel Eceran' }}
                      </span>
                      <h5 class="font-weight-bold text-white mb-1">
                        {{ $currentLocale === 'en' ? 'Custom Quota' : 'Kustom Kuota Kursi' }}
                      </h5>
                      <div class="h3 font-weight-bold text-warning mb-1">{{ $currentLocale === 'en' ? 'Custom' : 'Kustom' }}</div>
                      <div class="text-muted small mb-2">{{ ownerStore.numberOfPlayers }} {{ $t('quota.seats') }}</div>
                      <p class="text-light small mb-0" style="font-size: 11px;">
                        {{ $currentLocale === 'en' ? 'Specify exact employee seats count matching your team size.' : 'Tentukan kuota kursi anggota tim sendiri sesuai jumlah karyawan Anda.' }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- SECTION 2: COMPANY PROFILE & SEAT CONFIGURATION -->
              <div class="mb-4 pt-3 border-top" style="border-color: #2c2e33;">
                <h6 class="text-warning text-uppercase font-weight-bold mb-3 d-flex align-items-center" style="letter-spacing: 0.8px; font-size: 12px;">
                  <i class="mdi mdi-domain mr-2" style="font-size: 16px;"></i>
                  2. {{ $t('superadmin.companyInfo') }}
                </h6>
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label for="company-name" class="form-label text-light small font-weight-medium">
                      {{ $t('register.companyName') }} <span class="text-danger">*</span>
                    </label>
                    <input
                      id="company-name"
                      type="text"
                      class="form-control"
                      v-model="ownerStore.companyName"
                      :placeholder="$t('register.companyNamePlaceholder')"
                      required
                    />
                  </div>

                  <div class="col-md-6 mb-3">
                    <label for="phone-number" class="form-label text-light small font-weight-medium">
                      {{ $t('register.phoneNumber') }} <span class="text-danger">*</span>
                    </label>
                    <input
                      id="phone-number"
                      type="text"
                      class="form-control"
                      v-model="ownerStore.phoneNumber"
                      :placeholder="$t('register.phoneNumberPlaceholder')"
                      required
                    />
                  </div>

                  <!-- If Paketan: Choose Package Tier Dropdown -->
                  <div class="col-md-6 mb-3" v-if="selectedPlan === 'package'">
                    <label class="form-label text-light small font-weight-medium">
                      {{ $currentLocale === 'en' ? 'Package Plan Tier' : 'Tingkatan Paket' }} <span class="text-danger">*</span>
                    </label>
                    <select
                      class="form-control text-white"
                      style="background-color: #2a3038;"
                      v-model="selectedPackageId"
                      @change="onPackageSelect"
                    >
                      <option v-for="pkg in packages" :key="pkg.id" :value="pkg.id">
                        {{ pkg.package_name }} ({{ pkg.limit_user }} {{ $t('quota.seats') }} - Rp {{ formatPrice(pkg.price_per_person_per_month) }}/{{ $t('quota.seats') }})
                      </option>
                    </select>
                  </div>

                  <!-- Seat Quota Display / Input -->
                  <div class="col-md-6 mb-3">
                    <label for="number-of-players" class="form-label text-light small font-weight-medium">
                      {{ $t('register.numberOfPlayers') }} <span class="text-danger">*</span>
                    </label>
                    <div v-if="selectedPlan === 'trial'" class="input-group">
                      <input
                        type="text"
                        class="form-control text-warning font-weight-bold"
                        :value="$currentLocale === 'en' ? '15 Seats (Trial Included)' : '15 Kursi (Termasuk Uji Coba)'"
                        readonly
                      />
                      <span class="input-group-text bg-dark text-success border-secondary">
                        <i class="mdi mdi-check-circle mr-1"></i> {{ $currentLocale === 'en' ? 'Free' : 'Gratis' }}
                      </span>
                    </div>
                    <div v-else-if="selectedPlan === 'package'" class="input-group">
                      <input
                        type="text"
                        class="form-control text-info font-weight-bold"
                        :value="`${selectedPackageObj?.limit_user || 75} ${$t('quota.seats')}`"
                        readonly
                      />
                      <span class="input-group-text bg-dark text-muted border-secondary">{{ $t('quota.seats') }}</span>
                    </div>
                    <div v-else class="input-group">
                      <input
                        id="number-of-players"
                        type="number"
                        min="1"
                        max="2000"
                        class="form-control"
                        v-model.number="ownerStore.numberOfPlayers"
                        :placeholder="$t('register.numberOfPlayersPlaceholder')"
                        required
                      />
                      <span class="input-group-text bg-dark text-muted border-secondary">{{ $t('quota.seats') }}</span>
                    </div>
                  </div>

                  <!-- Subscription Duration -->
                  <div class="col-md-6 mb-3">
                    <label for="subscription-duration" class="form-label text-light small font-weight-medium">
                      {{ $t('register.subscription') }} <span class="text-danger">*</span>
                    </label>
                    <div v-if="selectedPlan === 'trial'">
                      <input
                        type="text"
                        class="form-control text-white"
                        :value="$currentLocale === 'en' ? '14 Days Active Trial' : '14 Hari Uji Coba Aktif'"
                        readonly
                      />
                    </div>
                    <select
                      v-else
                      id="subscription-duration"
                      class="form-control text-white"
                      style="background-color: #2a3038;"
                      v-model="ownerStore.subscription"
                      required
                    >
                      <option value="1 Month">1 {{ $t('register.monthUnit') }}</option>
                      <option value="3 Month">3 {{ $t('register.monthsUnit') }} ({{ $currentLocale === 'en' ? '5% Off' : 'Diskon 5%' }})</option>
                      <option value="6 Month">6 {{ $t('register.monthsUnit') }} ({{ $currentLocale === 'en' ? '10% Off' : 'Diskon 10%' }})</option>
                      <option value="12 Month">12 {{ $t('register.monthsUnit') }} ({{ $currentLocale === 'en' ? '15% Off - 1 Year' : 'Diskon 15% - 1 Tahun' }})</option>
                    </select>
                  </div>

                  <div class="col-12 mb-3">
                    <label for="company-logo" class="form-label text-light small font-weight-medium">
                      {{ $t('register.companyLogo') }} (PNG / JPG / SVG)
                    </label>
                    <input
                      id="company-logo"
                      type="file"
                      class="form-control dropify"
                      @change="handleFileChange"
                      ref="fileInput"
                      accept="image/*"
                    />
                  </div>
                </div>

                <!-- Live Pricing Telemetry Strip -->
                <div class="p-3 rounded mt-2" style="background: #12151e; border: 1px solid #2c2e33;">
                  <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div>
                      <span class="text-muted small d-block">
                        {{ selectedPlan === 'trial' ? ($currentLocale === 'en' ? 'Onboarding Plan Fee:' : 'Biaya Skema Pendaftaran:') : ($currentLocale === 'en' ? 'Estimated Total Payable:' : 'Estimasi Total Pembayaran:') }}
                      </span>
                      <div class="h4 font-weight-bold mb-0" :class="selectedPlan === 'trial' ? 'text-success' : 'text-warning'">
                        {{ selectedPlan === 'trial' ? ($currentLocale === 'en' ? 'Rp 0 (14-Day Free Trial)' : 'Rp 0 (Coba Gratis 14 Hari)') : `Rp ${formatPrice(calculatedGrandTotal)}` }}
                      </div>
                    </div>
                    <div class="text-end text-muted small">
                      <div>{{ calculatedSeats }} {{ $t('quota.seats') }} • {{ selectedPlan === 'trial' ? ($currentLocale === 'en' ? '14 Days' : '14 Hari') : formatSubscriptionLabel(ownerStore.subscription) }}</div>
                      <small class="text-success" v-if="selectedPlan === 'trial'">
                        <i class="mdi mdi-flash mr-1"></i> {{ $currentLocale === 'en' ? 'Instantly active upon registration' : 'Langsung aktif setelah pendaftaran' }}
                      </small>
                      <small class="text-info" v-else>
                        <i class="mdi mdi-receipt mr-1"></i> {{ $currentLocale === 'en' ? 'Includes 11% VAT & duration discount' : 'Termasuk PPN 11% & diskon durasi' }}
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- SECTION 3: OWNER / ADMINISTRATOR ACCOUNT -->
              <div class="mb-4 pt-3 border-top" style="border-color: #2c2e33;">
                <h6 class="text-warning text-uppercase font-weight-bold mb-3 d-flex align-items-center" style="letter-spacing: 0.8px; font-size: 12px;">
                  <i class="mdi mdi-account-tie mr-2" style="font-size: 16px;"></i>
                  3. {{ $t('superadmin.ownerContact') }}
                </h6>
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label for="owner-name" class="form-label text-light small font-weight-medium">
                      {{ $t('register.ownerName') }} <span class="text-danger">*</span>
                    </label>
                    <input
                      id="owner-name"
                      type="text"
                      class="form-control"
                      v-model="ownerStore.ownerName"
                      :placeholder="$t('register.ownerNamePlaceholder')"
                      required
                    />
                  </div>

                  <div class="col-md-6 mb-3">
                    <label for="register-email" class="form-label text-light small font-weight-medium">
                      {{ $t('register.email') }} <span class="text-danger">*</span>
                    </label>
                    <input
                      id="register-email"
                      type="email"
                      class="form-control"
                      v-model="ownerStore.emailAddress"
                      :placeholder="$t('register.emailPlaceholder')"
                      required
                    />
                  </div>

                  <div class="col-md-6 mb-3">
                    <label for="register-password" class="form-label text-light small font-weight-medium">
                      {{ $t('register.password') }} <span class="text-danger">*</span>
                    </label>
                    <div class="input-group">
                      <input
                        id="register-password"
                        :type="showPassword ? 'text' : 'password'"
                        class="form-control"
                        v-model="ownerStore.password"
                        :placeholder="$t('register.passwordPlaceholder')"
                        minlength="6"
                        required
                      />
                      <button
                        type="button"
                        class="btn btn-outline-secondary"
                        style="border-color: #2c2e33; background: #2a3038;"
                        @click="showPassword = !showPassword"
                        :title="showPassword ? ($currentLocale === 'id' ? 'Sembunyikan kata sandi' : 'Hide password') : ($currentLocale === 'id' ? 'Tampilkan kata sandi' : 'Show password')"
                      >
                        <i :class="showPassword ? 'mdi mdi-eye-off text-warning' : 'mdi mdi-eye text-muted'"></i>
                      </button>
                    </div>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label for="confirm-password" class="form-label text-light small font-weight-medium">
                      {{ $t('register.confirmPassword') }} <span class="text-danger">*</span>
                    </label>
                    <div class="input-group">
                      <input
                        id="confirm-password"
                        :type="showConfirmPassword ? 'text' : 'password'"
                        class="form-control"
                        v-model="confirmPassword"
                        :placeholder="$t('register.confirmPasswordPlaceholder')"
                        minlength="6"
                        required
                      />
                      <button
                        type="button"
                        class="btn btn-outline-secondary"
                        style="border-color: #2c2e33; background: #2a3038;"
                        @click="showConfirmPassword = !showConfirmPassword"
                        :title="showConfirmPassword ? ($currentLocale === 'id' ? 'Sembunyikan kata sandi' : 'Hide password') : ($currentLocale === 'id' ? 'Tampilkan kata sandi' : 'Show password')"
                      >
                        <i :class="showConfirmPassword ? 'mdi mdi-eye-off text-warning' : 'mdi mdi-eye text-muted'"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Included Enterprise Features Callout -->
              <div class="p-3 mb-4 rounded-lg" style="background: rgba(245, 158, 11, 0.08); border: 1px dashed rgba(245, 158, 11, 0.25);">
                <div class="text-warning small font-weight-bold mb-2 d-flex align-items-center">
                  <i class="mdi mdi-shield-star mr-1"></i>
                  {{ $t('register.plansBenefit') }}:
                </div>
                <div class="row text-muted small g-2">
                  <div class="col-md-4 d-flex align-items-center">
                    <i class="mdi mdi-check-circle text-success mr-1"></i>
                    {{ $t('register.benefit1') }}
                  </div>
                  <div class="col-md-4 d-flex align-items-center">
                    <i class="mdi mdi-check-circle text-success mr-1"></i>
                    {{ $t('register.benefit2') }}
                  </div>
                  <div class="col-md-4 d-flex align-items-center">
                    <i class="mdi mdi-check-circle text-success mr-1"></i>
                    {{ $t('register.benefit3') }}
                  </div>
                </div>
              </div>

              <!-- Error Message Display -->
              <div v-if="errorMessage" class="alert alert-danger py-2 small mb-3">
                <i class="mdi mdi-alert-circle mr-1"></i> {{ errorMessage }}
              </div>

              <!-- Action Submit Button -->
              <div class="text-center mb-3">
                <button
                  type="submit"
                  class="btn btn-warning btn-lg font-weight-bold text-dark w-100 py-3 shadow-sm d-flex align-items-center justify-content-center gap-2"
                  :disabled="loading"
                  style="border-radius: 8px;"
                >
                  <div
                    class="spinner-border spinner-border-sm text-dark mr-2"
                    role="status"
                    v-if="loading"
                  >
                    <span class="visually-hidden">{{ $t('common.loading') }}</span>
                  </div>
                  <i :class="selectedPlan === 'trial' ? 'mdi mdi-rocket-launch' : 'mdi mdi-check-decagram'" v-if="!loading"></i>
                  <span>
                    {{ selectedPlan === 'trial' ? ($currentLocale === 'en' ? 'Start 14-Day Free Trial (Instant Access)' : 'Mulai Coba Gratis 14 Hari (Akses Instan)') : ($currentLocale === 'en' ? 'Register & Proceed to Billing' : 'Daftar Ruang Kerja & Lanjut Pembayaran') }}
                  </span>
                </button>
              </div>

              <!-- Sign In Redirect -->
              <div class="text-center pt-2">
                <span class="text-muted small">{{ $t('register.alreadyRegistered') }} </span>
                <router-link :to="{ name: 'Login' }" class="text-warning font-weight-bold small text-decoration-none ml-1">
                  {{ $t('register.signIn') }} →
                </router-link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer Copyright -->
    <div class="text-center text-muted small py-2">
      © {{ new Date().getFullYear() }} {{ $t('auth.copyright') }}
    </div>
  </div>
</template>

<script>
import $ from "jquery";
import "dropify";
import { Store } from "@/services/regisServices";
import Swal from "sweetalert2";
import axios from "axios";
import config from "@/config/config";
import store from "@/store";

export default {
  name: "RegisterView",
  data() {
    return {
      selectedPlan: "trial", // "trial", "package", "custom"
      packages: [],
      selectedPackageId: 2,
      confirmPassword: "",
      errorMessage: "",
      showPassword: false,
      showConfirmPassword: false,
      ownerStore: {
        companyName: "",
        ownerName: "",
        emailAddress: "",
        phoneNumber: "",
        password: "",
        numberOfPlayers: 15,
        subscription: "12 Month",
        paymentStatus: "trial",
        companyLogo: null,
        domain: "",
      },
      loading: false,
    };
  },
  computed: {
    selectedPackageObj() {
      return this.packages.find((p) => p.id === this.selectedPackageId) || this.packages[0] || {
        package_name: "Business Pro",
        price_per_person_per_month: 49000,
        limit_user: 75,
      };
    },
    calculatedSeats() {
      if (this.selectedPlan === "trial") return 15;
      if (this.selectedPlan === "package") return this.selectedPackageObj?.limit_user || 75;
      return parseInt(this.ownerStore.numberOfPlayers) || 15;
    },
    calculatedMonths() {
      const parsed = parseInt(this.ownerStore.subscription) || 12;
      return parsed;
    },
    calculatedGrandTotal() {
      if (this.selectedPlan === "trial") return 0;
      const unitPrice = this.selectedPlan === "package"
        ? (parseFloat(this.selectedPackageObj?.price_per_person_per_month) || 49000)
        : 29000;
      
      const seats = this.calculatedSeats;
      const months = this.calculatedMonths;
      const subtotal = seats * unitPrice * months;

      // Duration discount
      let discountRate = 0;
      if (months === 3) discountRate = 0.05;
      if (months === 6) discountRate = 0.10;
      if (months === 12) discountRate = 0.15;

      const discountAmount = Math.round(subtotal * discountRate);
      const taxable = Math.max(0, subtotal - discountAmount);
      const ppn = Math.round(taxable * 0.11);
      return taxable + ppn;
    }
  },
  mounted() {
    this.fetchPackages();
    this.$nextTick(() => {
      try {
        const isEn = this.$currentLocale === 'en';
        $(".dropify").dropify({
          messages: {
            default: isEn ? "Drag and drop logo here or click to browse" : "Seret dan lepas logo di sini atau klik untuk memilih",
            replace: isEn ? "Drag and drop or click to replace" : "Seret dan lepas atau klik untuk mengganti",
            remove: isEn ? "Remove" : "Hapus",
            error: isEn ? "An error occurred with this file." : "Terjadi kesalahan pada berkas ini.",
          },
        });
      } catch (e) {
        console.warn("Dropify init:", e);
      }
    });
  },
  methods: {
    formatSubscriptionLabel(sub) {
      if (this.$currentLocale === 'en') return sub;
      return String(sub || '').replace(/Month/gi, 'Bulan').replace(/Days Trial/gi, 'Hari Uji Coba');
    },
    formatPrice(val) {
      if (!val) return "0";
      return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
    async fetchPackages() {
      try {
        const resp = await axios.get(`${config.API_URL}packages/public`);
        const list = Array.isArray(resp.data?.data) ? resp.data.data : (Array.isArray(resp.data) ? resp.data : []);
        if (list.length > 0) {
          this.packages = list;
          const pro = this.packages.find((p) => p.package_name?.toLowerCase().includes("pro")) || this.packages[0];
          this.selectedPackageId = pro.id;
        }
      } catch (err) {
        console.warn("Using preset packages fallback:", err);
      }
    },
    setPlan(plan) {
      this.selectedPlan = plan;
      if (plan === "trial") {
        this.ownerStore.numberOfPlayers = 15;
        this.ownerStore.paymentStatus = "trial";
        this.ownerStore.subscription = "14 Days Trial";
      } else if (plan === "package") {
        this.ownerStore.numberOfPlayers = this.selectedPackageObj?.limit_user || 75;
        this.ownerStore.paymentStatus = "paid";
        if (this.ownerStore.subscription === "14 Days Trial") {
          this.ownerStore.subscription = "12 Month";
        }
      } else {
        this.ownerStore.numberOfPlayers = 25;
        this.ownerStore.paymentStatus = "paid";
        if (this.ownerStore.subscription === "14 Days Trial") {
          this.ownerStore.subscription = "12 Month";
        }
      }
    },
    onPackageSelect() {
      if (this.selectedPlan === "package") {
        this.ownerStore.numberOfPlayers = this.selectedPackageObj?.limit_user || 75;
      }
    },
    handleFileChange(event) {
      if (event.target.files && event.target.files.length > 0) {
        this.ownerStore.companyLogo = event.target.files[0];
      } else {
        this.ownerStore.companyLogo = null;
      }
    },
    async submitForm() {
      // Validate password confirmation
      if (this.ownerStore.password !== this.confirmPassword) {
        this.errorMessage = this.$t('register.passwordMismatch') || (this.$currentLocale === 'en' ? "Passwords do not match." : "Konfirmasi kata sandi tidak cocok.");
        return;
      }
      if (this.ownerStore.password.length < 6) {
        this.errorMessage = this.$currentLocale === 'en'
          ? "Password must be at least 6 characters long."
          : "Kata sandi minimal harus 6 karakter.";
        return;
      }
      this.errorMessage = "";
      this.loading = true;

      try {
        const payload = {
          ...this.ownerStore,
          password_confirmation: this.confirmPassword,
          planType: this.selectedPlan,
          paymentStatus: this.selectedPlan === "trial" ? "trial" : "paid",
          numberOfPlayers: this.calculatedSeats,
        };

        const res = await Store(payload);

        // Store authentication token for immediate seamless login
        const token = res?.token || res?.access_token || res?.data?.token;
        if (token) {
          const userData = res.data || res.user || {
            role: "owner",
            name: this.ownerStore.ownerName,
            email: this.ownerStore.emailAddress,
          };
          store.commit("setAuthToken", token);
          store.commit("setTokenType", res.token_type || "Bearer");
          store.commit("setUser", userData);
          store.commit("login", token);
        }

        if (this.selectedPlan === "trial") {
          await Swal.fire({
            icon: "success",
            title: this.$currentLocale === 'en' ? "Welcome to Gamiteam!" : "Selamat Datang di Gamiteam!",
            text: this.$currentLocale === 'en'
              ? "Your 14-day free trial workspace (15 team member seats) is active. You can now invite your team and start tasks!"
              : "Ruang kerja uji coba 14 hari Anda (15 kursi anggota tim) telah aktif. Anda dapat langsung mengundang tim dan memulai misi kerja!",
            confirmButtonColor: "#f59e0b",
            confirmButtonText: this.$currentLocale === 'en' ? "Open Workspace Dashboard" : "Buka Dasbor Ruang Kerja",
            background: "#0f172a",
            color: "#fff",
          });
          this.$router.push({ name: "Dashboard" });
        } else {
          await Swal.fire({
            icon: "success",
            title: this.$currentLocale === 'en' ? "Workspace Registered!" : "Ruang Kerja Berhasil Didaftarkan!",
            text: this.$currentLocale === 'en'
              ? "Your enterprise workspace has been created. Proceeding to billing payment."
              : "Ruang kerja perusahaan Anda telah terdaftar. Anda akan diarahkan ke halaman checkout pembayaran kuota.",
            confirmButtonColor: "#f59e0b",
            confirmButtonText: this.$currentLocale === 'en' ? "Proceed to Payment" : "Lanjut ke Pembayaran",
            background: "#0f172a",
            color: "#fff",
          });
          this.$router.push({
            name: "BillingPay",
            query: { mode: this.selectedPlan === "custom" ? "addon" : "package" },
          });
        }
      } catch (error) {
        const errors = error.response?.data?.errors;
        let errMsg = "Registration failed. Please check your data.";
        if (Array.isArray(errors)) {
          errMsg = errors.join(", ");
        } else if (errors && typeof errors === "object") {
          errMsg = Object.values(errors).flat().join(", ");
        } else if (error.response?.data?.message) {
          errMsg = error.response.data.message;
        } else if (error.message) {
          errMsg = error.message;
        }

        this.errorMessage = errMsg;

        Swal.fire({
          icon: "error",
          title: this.$currentLocale === 'en' ? "Registration Failed" : "Pendaftaran Gagal",
          text: errMsg,
          confirmButtonColor: "#f59e0b",
          background: "#0f172a",
          color: "#fff",
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.admin-auth-root {
  background-color: #030a1a;
  min-height: 100vh;
  background-image: 
    radial-gradient(at 15% 15%, rgba(0, 212, 255, 0.14) 0px, transparent 50%),
    radial-gradient(at 85% 80%, rgba(255, 200, 36, 0.10) 0px, transparent 50%),
    linear-gradient(180deg, #061838 0%, #030a1a 100%);
}

.system-status-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: rgba(14, 38, 86, 0.85);
  border: 2px solid rgba(56, 189, 248, 0.35);
  border-radius: 9999px;
  color: #4ade80;
  font-family: 'Chakra Petch', sans-serif;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  box-shadow: 0 4px 0 #020816;
}

.status-dot {
  width: 7px;
  height: 7px;
  background: #4ade80;
  border-radius: 50%;
  box-shadow: 0 0 10px #4ade80;
}

.lang-switch-group {
  display: flex;
  align-items: center;
  background: #061534;
  border: 2px solid #2358b8;
  border-radius: 8px;
  padding: 2px 6px;
  box-shadow: 0 3px 0 #020816;
}

.lang-btn {
  background: transparent;
  border: none;
  color: #93c5fd;
  font-family: 'Chakra Petch', sans-serif;
  font-size: 11px;
  font-weight: 800;
  padding: 3px 8px;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.lang-btn.active {
  color: #1a0f00;
  background: linear-gradient(180deg, #ffe875 0%, #f59e0b 100%);
}

.lang-divider {
  color: #2358b8;
  font-size: 10px;
}

.player-link {
  color: #ffc824;
  font-family: 'Chakra Petch', sans-serif;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-decoration: none;
  padding: 6px 14px;
  border-radius: 8px;
  background: rgba(255, 200, 36, 0.14);
  border: 2px solid rgba(255, 200, 36, 0.45);
  box-shadow: 0 3px 0 #020816;
  transition: all 0.2s;
}

.player-link:hover {
  background: rgba(255, 200, 36, 0.25);
  color: #fff3a8;
}

.form-control {
  background-color: #041028 !important;
  border: 2px solid #1e4b9e !important;
  color: #f8fbff !important;
  border-radius: 10px;
  padding: 11px 14px;
  font-weight: 700;
  box-shadow: inset 0 3px 6px rgba(0, 0, 0, 0.55);
}

.form-control:focus {
  border-color: #00d4ff !important;
  box-shadow: 0 0 0 3px rgba(0, 212, 255, 0.22), inset 0 3px 6px rgba(0, 0, 0, 0.55) !important;
}

.form-control::placeholder {
  color: #5e84b8 !important;
}

.plan-card {
  background: linear-gradient(180deg, #0b2250 0%, #061534 100%);
  border: 2px solid #2358b8;
  border-radius: 12px;
  box-shadow: 0 4px 0 #020816;
  transition: all 0.25s ease;
  cursor: pointer;
}

.plan-card:hover {
  border-color: #38bdf8;
  transform: translateY(-2px);
}

.plan-card.plan-selected {
  border-color: #ffc824;
  background: linear-gradient(180deg, #163678 0%, #0b2250 100%);
  box-shadow: 0 4px 0 #4a2c00, 0 0 18px rgba(255, 200, 36, 0.25);
}
</style>
